// =============================================================================
// DPC Send Details (T-184) — PDP brochure capture modal
// =============================================================================
// Optimistic UI: instant toast + modal close on submit; fetch fires in
// background with keepalive: true so the user never waits for the backend.
// Failures recover via localStorage retry queue (~1h exp backoff) and a
// non-intrusive banner on the next page visit if retries exhaust.
//
// Tracking: ports the T-207 GA4 send() pattern — same MEASUREMENT_ID,
// same API_SECRET, same Content-Type: text/plain rule (avoids CORS
// preflight on mp/collect).
//
// Bot protection: honeypot field (display:none) + Cloudflare Turnstile.
// =============================================================================
(function () {
  'use strict';

  // ── Config ──────────────────────────────────────────────────────────────────
  // These are populated server-side by the modal Liquid snippet via data-*
  // attributes; default values are safe fallbacks for local linting only.
  const root = document.querySelector('[data-dpc-send-details]');
  if (!root) return;                                       // not on a PDP with brochure mechanism

  // Turnstile is registered for the production hostname only. Skip Turnstile
  // when we're on a non-production theme/host:
  //   1. window.Shopify.theme.role !== 'main' — set when previewing a test/
  //      unpublished theme. Shopify's preview link routes through the apex
  //      domain (deluxepetco.co.uk) via a session cookie, so hostname alone
  //      isn't enough — role is the gold-standard signal.
  //   2. hostname matches *.myshopify.com / *.shopifypreview.com — covers
  //      cases where the preview opens on those domains directly.
  function isProductionHost() {
    try {
      if (window.Shopify && window.Shopify.theme && window.Shopify.theme.role && window.Shopify.theme.role !== 'main') {
        return false;
      }
    } catch (_) {}
    const h = (location.hostname || '').toLowerCase();
    if (/\.myshopify\.com$/.test(h) || /\.shopifypreview\.com$/.test(h)) return false;
    return true;
  }

  const CONFIG = {
    submitEndpoint: root.dataset.submitEndpoint,           // form-submit-handler CF URL
    productGid:     root.dataset.productGid,
    productHandle:  root.dataset.productHandle,
    consentVersion: root.dataset.consentVersion || 'v1.0_2026-05-10',
    measurementId:  root.dataset.ga4Id || 'G-8JF1NE04VM',
    apiSecret:      root.dataset.ga4Secret,                // exposed via theme env or settings
    // Turnstile site key is registered for deluxepetco.co.uk, which covers
    // both live and test-theme preview URLs (test theme runs on apex with
    // ?preview_theme_id=). Always render the widget so the CF (which enforces
    // Turnstile in production mode) accepts the submit. 2026-05-13 fix.
    turnstileSiteKey: root.dataset.turnstileSiteKey || '',
    productTitle:   root.dataset.productTitle || '',
    petType:        (root.dataset.petType || 'pet').toLowerCase(), // 'cat' | 'dog' | 'pet'
    petMaxRows:     4,
    storageKey:     'dpc_brochure_queue',
    bannerKey:      'dpc_brochure_save_failed',
    savedKey:       'dpc_brochure_saved',                  // per-product saved flags
  };

  // ── DOM refs ────────────────────────────────────────────────────────────────
  const btn      = root.querySelector('.dpc-send-details-btn');
  const backdrop = document.querySelector('.dpc-modal-backdrop[data-dpc-modal]');
  const modal    = backdrop && backdrop.querySelector('.dpc-modal');
  const form     = modal && modal.querySelector('form[data-dpc-form]');
  if (!btn || !backdrop || !modal || !form) return;

  // ── Helpers ────────────────────────────────────────────────────────────────
  function uuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // RFC4122 v4 fallback (sub-1% of browsers without randomUUID)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function nowIso() {
    return new Date().toISOString();
  }

  function readForm() {
    const fd = new FormData(form);
    // Collect pet names from every pet_name[] input, trim, drop empties, dedup.
    const rawPetNames = fd.getAll('pet_name[]').map((v) => (v || '').toString().trim());
    const petNames = [];
    const seen = Object.create(null);
    for (let i = 0; i < rawPetNames.length; i++) {
      const n = rawPetNames[i];
      if (!n) continue;
      const key = n.toLowerCase();
      if (seen[key]) continue;
      seen[key] = true;
      petNames.push(n);
    }
    return {
      email: (fd.get('email') || '').toString().trim(),
      first_name: (fd.get('first_name') || '').toString().trim() || null,
      pet_names: petNames,
      pet_type: CONFIG.petType,
      consent: fd.get('consent') === 'on' || fd.get('consent') === 'true',
      honeypot: (fd.get('website') || '').toString(),     // honeypot field
      turnstile_token: (fd.get('cf-turnstile-response') || '').toString(),
    };
  }

  // Expose for tests — Playwright drives these from outside in regression
  window.__dpcSendDetails = { uuid, nowIso, readForm, CONFIG };

  // ── Focus trap ──────────────────────────────────────────────────────────────
  let lastFocused = null;

  function focusableInModal() {
    return modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    const fs = focusableInModal();
    if (!fs.length) return;
    const first = fs[0];
    const last  = fs[fs.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function openModal() {
    lastFocused = document.activeElement;
    backdrop.dataset.open = 'true';
    document.body.classList.add('dpc-modal-open');
    // NOTE: role/aria-modal/aria-labelledby are set on the modal in the Liquid
    // snippet (with the templated unique id `dpc-modal-title-{product.id}`).
    // Do not overwrite them here — a generic `dpc-modal-title` won't resolve
    // to an existing element and breaks the accessible-name link.
    // Defer focus until next frame so backdrop animation completes
    requestAnimationFrame(() => {
      const emailInput = form.querySelector('input[name="email"]');
      if (emailInput) emailInput.focus();
    });
    document.addEventListener('keydown', onKeydown);
    // Render Turnstile widget here (Task 11)
    if (typeof window.renderDpcTurnstile === 'function') window.renderDpcTurnstile();
    sendGa4('brochure_modal_opened', {});
  }

  function closeModal() {
    backdrop.dataset.open = 'false';
    document.body.classList.remove('dpc-modal-open');
    document.removeEventListener('keydown', onKeydown);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
      sendGa4('brochure_modal_dismissed', { reason: 'escape' });
      return;
    }
    trapFocus(e);
  }

  btn.addEventListener('click', function () {
    // If already saved this product (per localStorage flag), do nothing — button state
    // already shows "✓ Saved" from initial render.
    if (btn.dataset.saved === 'true') return;
    openModal();
  });

  backdrop.addEventListener('click', function (e) {
    // Click on backdrop (not inner modal) closes
    if (e.target === backdrop) {
      closeModal();
      sendGa4('brochure_modal_dismissed', { reason: 'backdrop_click' });
    }
  });

  const closeBtn = modal.querySelector('.dpc-modal__close');
  if (closeBtn) {
    closeBtn.addEventListener('click', function () {
      closeModal();
      sendGa4('brochure_modal_dismissed', { reason: 'close_button' });
    });
  }

  // ── GA4 send() (ported from T-207 dpc-checkout-funnel-pixel.js) ─────────────
  function getGa4ClientId() {
    try {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const c = cookies[i].trim();
        if (c.indexOf('_ga=') === 0) {
          const m = c.substring(3).match(/GA\d+\.\d+\.(\d+\.\d+)/);
          if (m && m[1]) return m[1];
        }
      }
    } catch (_) {}
    // Fallback: stable per-page random ID (events still arrive; just no session link)
    return Math.floor(Math.random() * 2147483647) + '.' + Math.floor(Date.now() / 1000);
  }

  // #211 (2026-10-24): the Measurement Protocol bypasses gtag's Consent Mode, so this sender must ask itself.
  // Same test as the gtag consent update in layout/theme.liquid: only an explicit analytics 'yes' counts;
  // no privacy API, no answer yet or a 'no' sends nothing.
  function analyticsAllowed() {
    try {
      const api = window.Shopify && window.Shopify.customerPrivacy;
      if (!api || typeof api.currentVisitorConsent !== 'function') return false;
      const c = api.currentVisitorConsent();
      return !!c && c.analytics === 'yes';
    } catch (_) { return false; }
  }

  function sendGa4(name, params) {
    try {
      if (!CONFIG.apiSecret) return;                                     // tracking disabled
      if (!analyticsAllowed()) return;                                   // #211: no analytics consent
      // Skip GA4 sends on preview / test theme (Shopify.theme.role !== 'main'
      // OR *.myshopify.com / *.shopifypreview.com hostname). Preview-mode
      // events should never pollute production analytics regardless of any
      // GA4-side IP filter rule, which may not catch every preview source
      // (mobile, VPN, agency networks).
      if (!isProductionHost()) return;
      const cid = getGa4ClientId();
      const payload = {
        client_id: cid,
        events: [{
          name: name,
          params: Object.assign({
            product_handle: CONFIG.productHandle,
            product_gid:    CONFIG.productGid,
          }, params || {}),
        }],
      };
      const endpoint = 'https://www.google-analytics.com/mp/collect'
        + '?measurement_id=' + encodeURIComponent(CONFIG.measurementId)
        + '&api_secret=' + encodeURIComponent(CONFIG.apiSecret);
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },                       // CORS-preflight avoidance
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(function () {});
    } catch (_) {}                                                       // tracking never throws into UX
  }

  // Expose for regression-test assertion
  window.__dpcSendDetails.sendGa4 = sendGa4;
  window.__dpcSendDetails.getGa4ClientId = getGa4ClientId;
  window.__dpcSendDetails.analyticsAllowed = analyticsAllowed;

  // ── Validation ──────────────────────────────────────────────────────────────
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  function setFieldError(input, msg) {
    input.setAttribute('aria-invalid', 'true');
    let hint = input.parentNode.querySelector('.dpc-modal__hint--error');
    if (!hint) {
      hint = document.createElement('p');
      hint.className = 'dpc-modal__hint dpc-modal__hint--error';
      hint.style.color = '#b00020';
      input.parentNode.appendChild(hint);
    }
    hint.textContent = msg;
  }

  function clearFieldError(input) {
    input.setAttribute('aria-invalid', 'false');
    const hint = input.parentNode.querySelector('.dpc-modal__hint--error');
    if (hint) hint.remove();
  }

  function setConsentError(visible) {
    const row = form.querySelector('[data-dpc-consent-row]');
    const msg = form.querySelector('[data-dpc-consent-error]');
    const input = form.querySelector('input[name="consent"]');
    if (row) row.dataset.error = visible ? 'true' : 'false';
    if (msg) msg.dataset.visible = visible ? 'true' : 'false';
    if (input) input.setAttribute('aria-invalid', visible ? 'true' : 'false');
  }

  function validate(values) {
    let ok = true;
    const emailInput  = form.querySelector('input[name="email"]');

    if (!values.email || !EMAIL_RE.test(values.email)) {
      setFieldError(emailInput, 'Please enter a valid email address.');
      ok = false;
    } else {
      clearFieldError(emailInput);
    }

    if (!values.consent) {
      setConsentError(true);
      ok = false;
    } else {
      setConsentError(false);
    }

    // Honeypot — if filled, silently fail (bot)
    if (values.honeypot) {
      ok = false;
    }

    // Turnstile — if site key configured, require a token UNLESS the widget
    // signalled a render/verify error (e.g. Cloudflare 600010 from strict
    // browser tracking prevention). In that "degraded" case, allow the submit
    // through with empty token + turnstile_unavailable=true flag; the CF
    // accepts it and applies stricter rate limiting on that path. 2026-05-13
    // (graceful degradation per Martin's customer-experience concern after
    // T-264 walkthrough showed strict-mode Edge breaks the widget).
    if (CONFIG.turnstileSiteKey && !values.turnstile_token && !_turnstileErrored) {
      setTurnstileError(true);
      ok = false;
    } else {
      setTurnstileError(false);
    }

    return ok;
  }

  function setTurnstileError(visible) {
    const container = modal.querySelector('.dpc-modal__turnstile');
    if (!container) return;
    let msg = container.parentNode.querySelector('[data-dpc-turnstile-error]');
    if (visible) {
      if (!msg) {
        msg = document.createElement('p');
        msg.setAttribute('data-dpc-turnstile-error', '');
        msg.className = 'dpc-modal__hint dpc-modal__hint--error';
        msg.setAttribute('role', 'alert');
        container.parentNode.insertBefore(msg, container.nextSibling);
      }
      msg.textContent = 'Please complete the verification above before sending.';
    } else if (msg) {
      msg.remove();
    }
  }

  // Clear consent error as soon as user ticks the box (no need to wait for re-submit)
  const _consentInput = form.querySelector('input[name="consent"]');
  if (_consentInput) {
    _consentInput.addEventListener('change', function () {
      if (_consentInput.checked) setConsentError(false);
    });
  }

  // Clear email error on input
  const _emailInput = form.querySelector('input[name="email"]');
  if (_emailInput) {
    _emailInput.addEventListener('input', function () {
      if (EMAIL_RE.test(_emailInput.value.trim())) clearFieldError(_emailInput);
    });
  }

  function buildPayload(values) {
    return {
      email: values.email,
      first_name: values.first_name,
      // Pet capture (T-184 V1 enhancement, optional fields)
      pet_names: values.pet_names || [],
      pet_count: (values.pet_names || []).length,
      pet_type: values.pet_type || 'pet',
      idempotency_key: uuid(),
      brochure_token: uuid(),
      product_handle: CONFIG.productHandle,
      product_title: CONFIG.productTitle,                 // for failure banner UX; CF ignores
      product_gid: CONFIG.productGid,
      consent_timestamp_utc: nowIso(),
      consent_ip_address: '',                              // server fills from headers
      consent_wording_version: CONFIG.consentVersion,
      consent_source_url: location.href,                   // stripped server-side per spec B12
      consent_age_affirmed: true,                          // affirmed by modal submit per S15 wording
      // Must match what the modal copy actually discloses. Modal (Task 13) says
      // "brochure plus occasional updates" + "newsletters (~2/month) and product
      // updates when relevant". back_in_stock + browse_abandonment are NOT
      // disclosed in V1 modal copy — adding them retroactively would weaken the
      // ICO audit posture (consent specific to purposes named). V1.1 modal copy
      // expansion can broaden this array.
      consent_marketing_purpose_disclosed: ['brochure', 'newsletter', 'product_updates'],
      turnstile_token: values.turnstile_token || '',
      turnstile_unavailable: _turnstileErrored,             // graceful degradation flag (2026-05-13)
    };
  }

  // ── Pet rows — adaptive label + dynamic add/remove (T-184 V1 enhancement) ──
  (function setupPetRows() {
    const wrap = form.querySelector('[data-dpc-pets]');
    if (!wrap) return;
    const rows = wrap.querySelector('[data-dpc-pet-rows]');
    const labelEl = wrap.querySelector('[data-dpc-pets-label]');
    const addBtn = wrap.querySelector('[data-dpc-pet-add]');
    const addLabel = wrap.querySelector('[data-dpc-pet-add-label]');

    // Adaptive copy based on detected pet type
    const singular = CONFIG.petType === 'cat' ? "Your cat's name (optional)"
                   : CONFIG.petType === 'dog' ? "Your dog's name (optional)"
                   : "Your pet's name (optional)";
    const plural = CONFIG.petType === 'cat' ? 'Your cats’ names (optional)'
                 : CONFIG.petType === 'dog' ? 'Your dogs’ names (optional)'
                 : 'Your pets’ names (optional)';
    const addAnother = CONFIG.petType === 'cat' ? 'Add another cat'
                     : CONFIG.petType === 'dog' ? 'Add another dog'
                     : 'Add another pet';
    if (labelEl) labelEl.textContent = singular;
    if (addLabel) addLabel.textContent = addAnother;

    function rowCount() {
      return rows.querySelectorAll('[data-dpc-pet-row]').length;
    }

    function updateState() {
      const count = rowCount();
      // Hide "Add" button at cap
      if (addBtn) addBtn.hidden = count >= CONFIG.petMaxRows;
      // Show/hide × buttons (only show if more than one row)
      rows.querySelectorAll('[data-dpc-pet-row]').forEach((row) => {
        const removeBtn = row.querySelector('[data-dpc-pet-remove]');
        if (!removeBtn) return;
        removeBtn.hidden = count <= 1;
      });
      // Label adapts singular/plural
      if (labelEl) labelEl.textContent = count > 1 ? plural : singular;
    }

    function makeRow() {
      const row = document.createElement('div');
      row.className = 'dpc-modal__pet-row';
      row.setAttribute('data-dpc-pet-row', '');
      const input = document.createElement('input');
      input.type = 'text';
      input.name = 'pet_name[]';
      input.setAttribute('aria-labelledby', 'dpc-pets-label'); /* T-654 B13 */
      input.className = 'dpc-modal__input dpc-modal__pet-input';
      input.autocomplete = 'off';
      input.maxLength = 40;
      input.setAttribute('data-dpc-pet-input', '');
      row.appendChild(input);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'dpc-modal__pet-remove';
      remove.setAttribute('data-dpc-pet-remove', '');
      remove.setAttribute('aria-label', 'Remove this name');
      remove.textContent = '×';                                  // ×
      remove.addEventListener('click', function () {
        if (rowCount() <= 1) return;
        row.remove();
        updateState();
      });
      row.appendChild(remove);
      return row;
    }

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        if (rowCount() >= CONFIG.petMaxRows) return;
        const row = makeRow();
        rows.appendChild(row);
        const input = row.querySelector('input');
        if (input) input.focus();
        updateState();
      });
    }

    // Initial state: ensure the first row has a remove button wired (so once
    // the user adds a second row, removing back to one is clean)
    const firstRow = rows.querySelector('[data-dpc-pet-row]');
    if (firstRow && !firstRow.querySelector('[data-dpc-pet-remove]')) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'dpc-modal__pet-remove';
      remove.setAttribute('data-dpc-pet-remove', '');
      remove.setAttribute('aria-label', 'Remove this name');
      remove.hidden = true;                                            // hidden when only 1 row
      remove.textContent = '×';
      remove.addEventListener('click', function () {
        if (rowCount() <= 1) return;
        firstRow.remove();
        updateState();
      });
      firstRow.appendChild(remove);
    }

    updateState();
  })();

  window.__dpcSendDetails.validate = validate;
  window.__dpcSendDetails.buildPayload = buildPayload;

  // ── Toast ────────────────────────────────────────────────────────────────────
  let toastEl = null;
  function showToast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'dpc-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.dataset.visible = 'true';
    setTimeout(function () { toastEl.dataset.visible = 'false'; }, 4500);
  }

  // ── Saved-state per product (localStorage flag; NOT consent-Functional per
  //    spec §3.4: this is reading the user's own prior action to avoid a
  //    duplicate save, exempt from PECR consent under Reg 6(4)(b) when used
  //    only to recover state of an action the user explicitly took).  ───────────
  function isAlreadySaved() {
    try {
      const raw = localStorage.getItem(CONFIG.savedKey);
      if (!raw) return false;
      const dict = JSON.parse(raw);
      return Boolean(dict[CONFIG.productHandle]);
    } catch (_) { return false; }
  }

  // Cap saved-state dict at 100 most-recent entries. Without this the dict
  // grows unbounded for power-users who save many products over time.
  function pruneSavedDict(dict) {
    const keys = Object.keys(dict);
    if (keys.length <= 100) return dict;
    const sorted = keys
      .map(k => ({ k: k, t: new Date(dict[k]).getTime() || 0 }))
      .sort((a, b) => b.t - a.t)            // most-recent first
      .slice(0, 100);
    const pruned = {};
    sorted.forEach(e => { pruned[e.k] = dict[e.k]; });
    return pruned;
  }

  function applySavedLabel() {
    // Swap the button text to the saved-state label (data-label-saved attr
    // on the <span>; default-state label is data-label-default).
    const span = btn.querySelector('.dpc-send-details-btn__label');
    if (span && span.dataset.labelSaved) {
      span.textContent = span.dataset.labelSaved;
    }
  }

  function markSaved() {
    try {
      const raw = localStorage.getItem(CONFIG.savedKey);
      const dict = pruneSavedDict(raw ? JSON.parse(raw) : {});
      dict[CONFIG.productHandle] = nowIso();
      localStorage.setItem(CONFIG.savedKey, JSON.stringify(dict));
    } catch (_) {}
    btn.dataset.saved = 'true';
    btn.setAttribute('aria-label', 'Brochure sent');
    applySavedLabel();
  }

  // On script init, restore button state if previously saved
  if (isAlreadySaved()) {
    btn.dataset.saved = 'true';
    btn.setAttribute('aria-label', 'Brochure sent');
    applySavedLabel();
  }

  // ── Submit handler ──────────────────────────────────────────────────────────
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    const values = readForm();
    if (!validate(values)) {
      sendGa4('brochure_save_validation_failed', {});
      return;
    }
    // Clear any stale failure banner before optimistic UI — user is actively
    // retrying so any prior banner is obsolete. If THIS attempt fails, the
    // 'fatal' branch below will re-flag.
    clearFailureBanner();

    const payload = buildPayload(values);

    // OPTIMISTIC UI — user sees instant feedback
    showToast('Your brochure is on its way. Check your inbox shortly.');
    closeModal();
    markSaved();
    sendGa4('brochure_save_submit', {
      brochure_token: payload.brochure_token,
      idempotency_key: payload.idempotency_key,
    });

    // Persist to retry queue BEFORE first fetch so a network failure during
    // the request itself is recoverable. Once the fetch reports 200, the
    // queue handler removes the entry.
    queueAdd(payload);

    // Fire-and-forget fetch (keepalive ensures survival of navigation)
    submitOnce(payload).then(function (status) {
      if (status === 'ok') {
        queueRemove(payload.idempotency_key);
        clearFailureBanner();                                          // late-arriving success clears any earlier-flagged banner
      } else if (status === 'duplicate') {
        queueRemove(payload.idempotency_key);                          // already processed
        clearFailureBanner();
      } else if (status === 'gate') {
        // 410 from CF — product not ready. Surface to user; remove from queue.
        queueRemove(payload.idempotency_key);
        btn.dataset.saved = 'false';
        showToast('This product is being updated — please try again later.');
      } else if (status === 'fatal') {
        queueRemove(payload.idempotency_key);
        flagFailureBanner(CONFIG.productTitle || CONFIG.productHandle);
      }
      // 'retry' status leaves the entry in the queue; retry handler picks it up
    });
  });

  // ── submitOnce (single fetch attempt; returns status code) ──────────────────
  async function submitOnce(payload) {
    _submitInFlight = true;
    try {
      const resp = await fetch(CONFIG.submitEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'omit',
      });
      if (resp.status === 200) return 'ok';
      if (resp.status === 410) return 'gate';                          // product_not_ready
      if (resp.status === 400) return 'fatal';                         // validation_failed; client bug — alert user
      if (resp.status >= 500 || resp.status === 0) return 'retry';
      return 'fatal';
    } catch (_) {
      return 'retry';                                                  // network error → retry
    } finally {
      _submitInFlight = false;
    }
  }

  window.__dpcSendDetails.submitOnce = submitOnce;

  // ── Retry queue (PECR Reg 6(4)(b) strictly-necessary: persists failed
  //    submit payloads to recover from transient network error of an action
  //    the user explicitly initiated). ──────────────────────────────────────
  function queueRead() {
    try {
      const raw = localStorage.getItem(CONFIG.storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  }

  function queueWrite(items) {
    try {
      localStorage.setItem(CONFIG.storageKey, JSON.stringify(items));
    } catch (_) {}
  }

  function queueAdd(payload) {
    let items = queueRead();
    // Prune any stale items for the SAME product — user is actively
    // re-submitting, any pending retry for this product is now obsolete.
    // Without this, an old failed payload's retry can fire AFTER the new
    // submit succeeds, returning fatal (consumed Turnstile token / 403)
    // and re-flagging the failure banner even though Klaviyo got the
    // event. 2026-05-13 (T-184) after Martin reproduced the zombie-retry
    // → banner-resurrection pattern.
    items = items.filter(function (it) {
      return it.payload && it.payload.product_handle !== payload.product_handle;
    });
    // Cap queue size to prevent unbounded growth from pathological loops
    if (items.length >= 20) items.shift();
    items.push({
      payload: payload,
      attempts: 0,
      firstAttemptAt: Date.now(),
      nextAttemptAt: Date.now(),
    });
    queueWrite(items);
  }

  function queueRemove(idempotency_key) {
    queueWrite(queueRead().filter(function (it) {
      return it.payload.idempotency_key !== idempotency_key;
    }));
  }

  // In-flight guard — set to true while a submit is awaiting a response from
  // the CF, set to false in submitOnce.then(). Prevents processQueue from
  // retrying an item that is CURRENTLY in flight. Root cause of the
  // 2026-05-13 "instant success then 15s later error banner" report: the
  // CF can take 30-60s on cold start (sequential Turnstile + Firestore +
  // Shopify metafields + Klaviyo). Without this guard, setInterval fires
  // processQueue mid-flight → retry submits the same payload → Cloudflare
  // rejects the consumed Turnstile token → 403 → fatal → banner flagged.
  // The original submit then finally returns 200 ~35s later and clears
  // the banner, but the user has already seen the alarming UX.
  // Long-term fix: T-313 Pub/Sub split of the form_submit_handler CF.
  let _submitInFlight = false;

  // Backoff schedule (ms from firstAttemptAt): 0, 5s, 30s, 2m, 10m, 30m. Total ~42m.
  const BACKOFF_MS = [0, 5e3, 30e3, 2 * 60e3, 10 * 60e3, 30 * 60e3];
  const MAX_ATTEMPTS = BACKOFF_MS.length;
  // Silent age-eviction: items older than 1h are dropped without flagging the
  // banner — they're stale (user has moved on, Turnstile token long-expired,
  // page reload after page reload would otherwise keep retrying forever).
  const QUEUE_MAX_AGE_MS = 60 * 60 * 1000;

  async function processQueue() {
    // Skip entirely if a submit is currently in flight — the in-flight
    // payload is in the queue but submitOnce.then() hasn't fired yet
    // (slow CF). Retrying now would duplicate-submit the same payload
    // with the same Turnstile token → Cloudflare rejects 2nd call → 403
    // → spurious failure banner even though the 1st call ultimately
    // succeeds. Bug reproduced 2026-05-13: CF took 51.7s on cold start,
    // setInterval fired processQueue at +15s into that flight.
    if (_submitInFlight) return;
    const items = queueRead();
    const now = Date.now();
    let mutated = false;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      // Silent prune: items past max age get dropped without flagging banner
      if (it.firstAttemptAt && (now - it.firstAttemptAt) > QUEUE_MAX_AGE_MS) {
        items.splice(i, 1);
        i--;
        mutated = true;
        continue;
      }
      if (it.attempts >= MAX_ATTEMPTS) {
        // Exhausted — remove and flag banner
        flagFailureBanner(it.payload.product_title || it.payload.product_handle);
        items.splice(i, 1);
        i--;
        mutated = true;
        continue;
      }
      if (now < it.nextAttemptAt) continue;
      it.attempts++;
      it.nextAttemptAt = it.firstAttemptAt + BACKOFF_MS[Math.min(it.attempts, MAX_ATTEMPTS - 1)];
      mutated = true;
      const status = await submitOnce(it.payload);
      if (status === 'ok' || status === 'duplicate' || status === 'gate' || status === 'fatal') {
        items.splice(i, 1);
        i--;
        if (status === 'gate' || status === 'fatal') {
          flagFailureBanner(it.payload.product_title || it.payload.product_handle);
        } else {
          // 'ok' or 'duplicate' — clear any banner an earlier failure may have set
          clearFailureBanner();
        }
      }
    }
    if (mutated) queueWrite(items);
  }

  // Drain queue on page load + every 30s while page is open
  document.addEventListener('DOMContentLoaded', processQueue);
  setInterval(processQueue, 30e3);

  window.__dpcSendDetails.queue = { read: queueRead, add: queueAdd, remove: queueRemove, process: processQueue };
  // Test hooks for the in-flight guard
  window.__dpcSendDetails.setSubmitInFlight = function (v) { _submitInFlight = !!v; };
  window.__dpcSendDetails.getSubmitInFlight = function () { return _submitInFlight; };

  // ── Failure banner (PECR Reg 6(4)(b) strictly-necessary per spec §3.4) ─────
  function flagFailureBanner(productHandle) {
    try {
      localStorage.setItem(CONFIG.bannerKey, JSON.stringify({
        product: productHandle,
        ts: nowIso(),
      }));
    } catch (_) {}
    renderFailureBanner();
  }

  // Clear any visible failure banner + remove from localStorage. Called when
  // a fresh submit attempt starts (user is actively retrying, any prior
  // banner is now obsolete) and when a submit returns success ('ok' /
  // 'duplicate') so a stale banner from earlier doesn't linger after a
  // successful save. 2026-05-13 (T-184) after Martin saw a banner appear
  // post-Klaviyo-success because an earlier failure had left a stale flag.
  function clearFailureBanner() {
    try { localStorage.removeItem(CONFIG.bannerKey); } catch (_) {}
    const banner = document.querySelector('.dpc-fail-banner');
    if (banner) banner.dataset.visible = 'false';
  }

  function renderFailureBanner() {
    let raw;
    try { raw = localStorage.getItem(CONFIG.bannerKey); } catch (_) { return; }
    if (!raw) return;
    let data;
    try { data = JSON.parse(raw); } catch (_) { return; }

    let banner = document.querySelector('.dpc-fail-banner');
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'dpc-fail-banner';
      banner.innerHTML =
        '<span class="dpc-fail-banner__msg"></span> ' +
        '<a href="mailto:support@deluxepetco.co.uk" style="color:#1a1a1a;text-decoration:underline;">Email us</a>' +
        '<button type="button" class="dpc-fail-banner__close" aria-label="Dismiss">×</button>';
      document.body.appendChild(banner);
      banner.querySelector('.dpc-fail-banner__close').addEventListener('click', function () {
        try { localStorage.removeItem(CONFIG.bannerKey); } catch (_) {}
        banner.dataset.visible = 'false';
      });
    }
    const msg = banner.querySelector('.dpc-fail-banner__msg');
    msg.textContent = 'We had trouble saving the brochure for ' + (data.product || 'this product') + '. ';
    banner.dataset.visible = 'true';
  }

  // Show banner on page load if flag set
  document.addEventListener('DOMContentLoaded', renderFailureBanner);

  window.__dpcSendDetails.banner = { flag: flagFailureBanner, render: renderFailureBanner, clear: clearFailureBanner };

  // ── Cloudflare Turnstile (free tier, ~1M validations/month) ────────────────
  // Site key passed via data-turnstile-site-key on the modal root. Skip if empty.
  let turnstileWidgetId = null;
  // Tracks whether Turnstile widget signalled an error (600010, network fail,
  // strict-tracking-prevention block, etc). When true, submit proceeds with
  // empty token + turnstile_unavailable=true; CF rate-limits this path.
  let _turnstileErrored = false;
  // Test hook — regression module forces the degraded path by setting this true.
  window.__dpcSendDetails.simulateTurnstileError = function () { _turnstileErrored = true; };
  window.__dpcSendDetails.getTurnstileErrored = function () { return _turnstileErrored; };

  function ensureTurnstileScript() {
    if (!CONFIG.turnstileSiteKey) return Promise.resolve(false);
    if (window.turnstile) return Promise.resolve(true);
    return new Promise(function (resolve) {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.defer = true;
      s.onload = function () { resolve(true); };
      s.onerror = function () { resolve(false); };
      document.head.appendChild(s);
    });
  }

  window.renderDpcTurnstile = function () {
    if (!CONFIG.turnstileSiteKey) return;
    ensureTurnstileScript().then(function (loaded) {
      if (!loaded || !window.turnstile) return;
      const container = modal.querySelector('.dpc-modal__turnstile');
      if (!container) return;
      if (turnstileWidgetId !== null) {
        window.turnstile.reset(turnstileWidgetId);
        return;
      }
      turnstileWidgetId = window.turnstile.render(container, {
        sitekey: CONFIG.turnstileSiteKey,
        theme: 'light',
        size: 'normal',
        callback: function (token) {
          const hidden = form.querySelector('input[name="cf-turnstile-response"]');
          if (hidden) hidden.value = token;
        },
        'error-callback': function () {
          _turnstileErrored = true;
          sendGa4('brochure_turnstile_error', {});
        },
        'expired-callback': function () {
          const hidden = form.querySelector('input[name="cf-turnstile-response"]');
          if (hidden) hidden.value = '';
        },
      });
    });
  };

  // Sentinel: confirm script loaded
  root.dataset.dpcLoaded = '1';
})();
